
import React, { useEffect, useState } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { useAuth } from '@/contexts/AuthContext';
import { tokenService } from '@/services/payment';
import { Button } from '@/components/ui/button';
import { CheckCircle, Loader2 } from 'lucide-react';
import { motion } from 'framer-motion';
import { useToast } from '@/hooks/use-toast';

// Helper to parse URL query parameters
const useQuery = () => {
  return new URLSearchParams(useLocation().search);
};

const PaymentSuccess = () => {
  const query = useQuery();
  const navigate = useNavigate();
  const { currentUser } = useAuth();
  const { toast } = useToast();
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [retryCount, setRetryCount] = useState(0);
  const [creditsUpdated, setCreditsUpdated] = useState(false);
  
  const orderId = query.get('order_id');
  
  useEffect(() => {
    const verifyAndProcessPayment = async () => {
      if (!currentUser) {
        setError('User authentication required');
        setLoading(false);
        return;
      }

      if (!orderId) {
        setError('Missing order information');
        setLoading(false);
        return;
      }

      try {
        console.log('[PAYMENT_SUCCESS] Verifying payment:', {
          orderId,
          userId: currentUser.uid
        });
        
        // Verify and process the payment
        const success = await tokenService.verifyCreemPayment(orderId, currentUser.uid);
        
        if (success) {
          setCreditsUpdated(true);
          
          // Show success toast
          toast({
            title: 'Payment Successful!',
            description: 'Your credits have been added to your account.',
          });
          
          // Dispatch an event to notify credit components to update
          window.dispatchEvent(new CustomEvent('payment-success'));
          
          // Small delay to ensure UI updates are visible
          setTimeout(() => {
            setLoading(false);
            navigate('/'); // Redirect to home after successful payment
          }, 1500);
        } else {
          throw new Error('Payment verification failed');
        }
        
      } catch (error) {
        console.error('[PAYMENT_SUCCESS] Error:', error);
        
        // If we haven't tried too many times, schedule a retry
        if (retryCount < 3) {
          console.log('[PAYMENT_SUCCESS] Scheduling retry attempt', retryCount + 1);
          setRetryCount(prev => prev + 1);
          setTimeout(() => {
            verifyAndProcessPayment();
          }, Math.min(2000 * Math.pow(2, retryCount), 10000)); // Exponential backoff
          return;
        }
        
        setError('Failed to process payment. Please contact support.');
        toast({
          title: 'Payment Error',
          description: 'There was a problem adding credits to your account. Please contact support.',
          variant: 'destructive',
        });
        
        setLoading(false);
      }
    };
    
    verifyAndProcessPayment();
  }, [currentUser, orderId, toast, retryCount, navigate]);
  
  // If we're still loading after 30 seconds, show an error
  useEffect(() => {
    const timeout = setTimeout(() => {
      if (loading && !creditsUpdated) {
        setError('The process is taking longer than expected. Please contact support.');
        setLoading(false);
      }
    }, 30000);

    return () => clearTimeout(timeout);
  }, [loading, creditsUpdated]);

  const handleRetry = () => {
    setLoading(true);
    setError(null);
    setRetryCount(0);
  };

  return (
    <div className="min-h-screen bg-gradient-to-b from-background to-background/90 flex items-center justify-center p-4">
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.5 }}
        className="max-w-md w-full bg-card rounded-lg border border-border shadow-lg p-8"
      >
        {loading ? (
          <div className="text-center py-12">
            <Loader2 className="h-12 w-12 animate-spin text-primary mx-auto mb-4" />
            <h2 className="text-2xl font-bold mb-2">Processing Payment</h2>
            <p className="text-muted-foreground">
              {retryCount > 0 
                ? `Retrying to add credits (Attempt ${retryCount}/3)...` 
                : 'Verifying your payment...'}
            </p>
          </div>
        ) : error ? (
          <div className="text-center py-12">
            <div className="h-12 w-12 rounded-full bg-red-100 flex items-center justify-center mx-auto mb-4">
              <span className="text-red-500 text-2xl">!</span>
            </div>
            <h2 className="text-2xl font-bold mb-2">Payment Error</h2>
            <p className="text-muted-foreground mb-6">{error}</p>
            <div className="flex flex-col gap-4">
              <Button variant="default" onClick={handleRetry}>
                Retry Processing
              </Button>
              <Button variant="default" onClick={() => navigate('/pricing')}>
                Return to Pricing
              </Button>
              <Button variant="outline" onClick={() => navigate('/support')}>
                Contact Support
              </Button>
            </div>
          </div>
        ) : (
          <div className="text-center py-8">
            <div className="bg-green-100 h-16 w-16 rounded-full flex items-center justify-center mx-auto mb-6">
              <CheckCircle className="h-10 w-10 text-green-500" />
            </div>
            <h2 className="text-2xl font-bold mb-2">Payment Successful!</h2>
            <p className="text-muted-foreground mb-8">
              Thank you for your purchase. Your credits have been added to your account.
            </p>
            <p className="text-sm text-muted-foreground mb-4">
              Redirecting you to the dashboard...
            </p>
          </div>
        )}
      </motion.div>
    </div>
  );
};

export default PaymentSuccess;
